import { api } from "../../lib";
import type { SourceSubsite } from "./sourcesubsites.service";
import {
  OrganMapPositionUpdateQueue,
  type OrganMapPositionUpdate,
} from "./OrganMapPositionUpdateQueue";

export interface OrganMapTemplate {
  id: string;
  name: string;
  description: string | null;
  organName: string;
  svgContent: string | null;
  imageUrl: string | null;
  width: number | null;
  height: number | null;
  isActive: boolean;
  linkedSourcesCount?: number;
  positionedSubsitesCount?: number;
  createdAt: string;
  updatedAt: string;
}

export type LinkedSourceSubsite = Pick<
  SourceSubsite,
  | "id"
  | "name"
  | "description"
  | "sourceId"
  | "isActive"
  | "isDefault"
  | "positionX"
  | "positionY"
>;

export interface LinkedSource {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  organMapTemplateId: string | null;
  subsites: LinkedSourceSubsite[];
}

export interface CreateOrganMapTemplateInput {
  name: string;
  description?: string;
  organName: string;
  svgContent?: string;
  imageUrl?: string;
  width?: number;
  height?: number;
  isActive?: boolean;
}

export interface UpdateOrganMapTemplateInput
  extends Partial<CreateOrganMapTemplateInput> {
  id: string;
}

export interface OrganMapPresignedUrlResponse {
  url: string;
  key: string;
}

export interface UpdateSubsitePositionInput extends OrganMapPositionUpdate {
  templateId: string;
}

const persistSubsitePosition = (
  input: UpdateSubsitePositionInput
): Promise<LinkedSourceSubsite> =>
  api.put<LinkedSourceSubsite>(
    `/admin/organ-map-templates/${input.templateId}/subsite-position`,
    {
      subsiteId: input.subsiteId,
      positionX: input.positionX,
      positionY: input.positionY,
    }
  );

const positionUpdateQueue = new OrganMapPositionUpdateQueue<
  UpdateSubsitePositionInput,
  LinkedSourceSubsite
>(persistSubsitePosition);

export const organMapTemplatesService = {
  getPresignedUrl: async (
    fileName: string,
    contentType: string
  ): Promise<OrganMapPresignedUrlResponse> => {
    return api.post<OrganMapPresignedUrlResponse>(
      "/documents/presigned-url/organ-map-templates",
      { fileName, contentType }
    );
  },

  uploadToS3: async (url: string, file: File): Promise<void> => {
    await fetch(url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });
  },

  getDownloadUrl: async (key: string): Promise<{ url: string }> => {
    return api.post<{ url: string }>("/documents/download-url", { key });
  },

  getAll: async () => {
    return api.get<OrganMapTemplate[]>("/admin/organ-map-templates");
  },

  getById: async (id: string) => {
    return api.get<OrganMapTemplate>(`/admin/organ-map-templates/${id}`);
  },

  create: async (input: CreateOrganMapTemplateInput) => {
    return api.post<OrganMapTemplate>("/admin/organ-map-templates", {
      name: input.name,
      description: input.description,
      organName: input.organName,
      svgContent: input.svgContent,
      imageUrl: input.imageUrl,
      width: input.width,
      height: input.height,
      isActive: input.isActive ?? true,
    });
  },

  update: async (input: UpdateOrganMapTemplateInput) => {
    const { id, ...updates } = input;
    return api.put<OrganMapTemplate>(
      `/admin/organ-map-templates/${id}`,
      updates
    );
  },

  toggleActive: async (id: string, isActive: boolean) => {
    return api.put<OrganMapTemplate>(`/admin/organ-map-templates/${id}`, {
      isActive,
    });
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/organ-map-templates/${id}`);
  },

  getLinkedSources: async (templateId: string) => {
    return api.get<LinkedSource[]>(
      `/admin/organ-map-templates/${templateId}/linked-sources`
    );
  },

  linkSources: async (templateId: string, sourceIds: string[]) => {
    return api.put<{ message: string }>(
      `/admin/organ-map-templates/${templateId}/link-sources`,
      { sourceIds }
    );
  },

  updateSubsitePosition: async (input: UpdateSubsitePositionInput) =>
    positionUpdateQueue.enqueue(input),

  updateSubsitePositions: async (
    templateId: string,
    positions: Array<{
      subsiteId: string;
      positionX: number | null;
      positionY: number | null;
    }>
  ) => {
    return api.put<{ message: string }>(
      `/admin/organ-map-templates/${templateId}/subsite-positions`,
      { positions }
    );
  },
};
